import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { Check } from "lucide-react";
import { SiYoutube } from "react-icons/si";
import {
  DEMO_SOURCE,
  DEMO_STEPS,
  CURRENT_FOLDER,
  MONTH_FOLDERS,
  MONTH_TASKS,
  REFLECT_PAGE,
  TRIGGERS,
  TRIGGER_QUESTIONS,
  nw,
} from "@/components/landing/landing-data";
import { ReadyBadge, TriggerChip } from "@/components/landing/questions-section";
import {
  TYPE_TICK_MS,
  useLandingReducedMotion,
  useOnScreen,
  usePageVisible,
} from "@/components/landing/landing-motion";

/** After the visitor clicks something, auto-play waits this long before restarting. */
const RESUME_AFTER_MS = 4000;

/**
 * Elapsed time of a panel's little story. It only advances while auto-play is running,
 * so the story freezes off screen and resumes where it left off.
 */
function useStoryClock(playing: boolean) {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (!playing) return;
    const id = window.setInterval(() => setT((v) => Math.min(v + TYPE_TICK_MS, 10000)), TYPE_TICK_MS);
    return () => window.clearInterval(id);
  }, [playing]);
  return t;
}

/** Staggered entrance for elements inside a scripted panel. */
const popAt = (ms: number): CSSProperties => ({ animationDelay: `${ms}ms` });

interface PanelProps {
  /** A click inside the panel: auto-play pauses and the panel keeps the visitor's choice. */
  onInteract: () => void;
  /** Auto-play was on when this panel opened, so it tells its story (else it shows a static state). */
  auto: boolean;
  /** Auto-play is running right now (on screen, tab visible): advance the story. */
  playing: boolean;
}

/** Drop it in: the link types itself, then "Listening…" appears. */
function DropItIn({ auto, playing }: PanelProps) {
  const [scripted] = useState(auto);
  const t = useStoryClock(playing && scripted);
  const chars = scripted ? Math.min(DEMO_SOURCE.length, Math.floor(t / TYPE_TICK_MS)) : DEMO_SOURCE.length;
  const typed = chars >= DEMO_SOURCE.length;

  return (
    <div className="flex w-full flex-col items-center gap-6">
      <div className="lp-urlpill w-full max-w-[640px] text-[15px] sm:min-h-16 sm:text-[17px]">
        <SiYoutube className="h-6 w-6 shrink-0 text-(--lp-ink)" aria-hidden />
        <span className="min-w-0 flex-1 truncate">
          <span className="sr-only">{DEMO_SOURCE}</span>
          <span aria-hidden>{DEMO_SOURCE.slice(0, chars)}</span>
          <span className="lp-caret lp-caret-blink" aria-hidden />
        </span>
      </div>
      <p
        className={`flex items-center gap-3.5 text-[15px] text-(--lp-muted) transition-opacity duration-500 sm:text-base ${
          typed ? "opacity-100" : "opacity-0"
        }`}
      >
        <span className="lp-spinner lp-spinner-spin" aria-hidden />
        Listening for what matters to you…
      </p>
    </div>
  );
}

/** Unfold it: triggers surface, then each one is picked in turn and its question appears. */
function UnfoldIt({ onInteract, auto, playing }: PanelProps) {
  const [scripted] = useState(auto);
  const [picked, setPicked] = useState<number | null>(null);
  const t = useStoryClock(playing && scripted && picked === null);
  const storyTrigger = scripted ? Math.min(TRIGGERS.length - 1, Math.floor(Math.max(0, t - 600) / 2000)) : 0;
  const trigger = picked ?? storyTrigger;

  return (
    <div className="relative w-full">
      <svg
        viewBox="0 0 1000 560"
        preserveAspectRatio="none"
        className="pointer-events-none absolute inset-0 hidden h-full w-full md:block"
        aria-hidden
      >
        <path className="lp-conn lp-conn-flow" d="M260 250 C 170 200, 190 120, 300 90" />
        <path className="lp-conn lp-conn-flow" d="M740 250 C 830 200, 810 120, 700 90" />
        <path className="lp-conn lp-conn-flow" d="M660 450 C 760 440, 790 380, 740 340" />
      </svg>
      <div className="relative mx-auto flex max-w-[560px] flex-col items-center gap-3 rounded-[30px] bg-white/80 px-4 py-4 shadow-[0_20px_50px_-30px_rgba(47,41,37,0.35)] sm:px-[22px]">
        <div className="lp-urlpill w-full text-[15px] sm:text-[17px]">
          <SiYoutube className="h-6 w-6 shrink-0 text-(--lp-ink)" aria-hidden />
          <span className="min-w-0 flex-1 truncate">{DEMO_SOURCE}</span>
        </div>
        <ReadyBadge className={scripted ? "lp-pop" : ""} style={popAt(150)} />
      </div>
      <div role="group" aria-label="Choose a trigger" className="relative mt-6 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-5">
        {TRIGGERS.map((t, i) => (
          <button
            key={t.line1}
            type="button"
            className={`lp-trig ${scripted ? "lp-pop" : ""}`}
            style={popAt(300 + i * 120)}
            aria-pressed={trigger === i}
            onClick={() => {
              onInteract();
              setPicked(i);
            }}
          >
            <TriggerChip index={i} />
          </button>
        ))}
      </div>
      <p
        key={trigger}
        className="lp-pop relative mx-auto mt-7 max-w-[760px] text-center text-xl font-semibold leading-[1.4] text-(--lp-ink) sm:text-2xl"
        aria-live="polite"
      >
        “{TRIGGER_QUESTIONS[trigger]}”
      </p>
    </div>
  );
}

/**
 * A photo cropped out of the approved journal artwork (journal-collage.webp, 1448 × 1086),
 * so the Reflect page reuses imagery the site already ships.
 */
const JOURNAL_IMG = "/images/landing/v6/journal-collage.webp";
function cropStyle([x, y, w, h]: [number, number, number, number]): CSSProperties {
  return {
    backgroundImage: `url("${JOURNAL_IMG}")`,
    backgroundSize: `${(1448 / w) * 100}% auto`,
    backgroundPosition: `${(x / (1448 - w)) * 100}% ${(y / (1086 - h)) * 100}%`,
    aspectRatio: `${w} / ${h}`,
  };
}
const PHOTOS: { crop: [number, number, number, number]; caption: string }[] = [
  { crop: [140, 160, 96, 104], caption: "new season" },
  { crop: [386, 632, 108, 100], caption: "good days" },
];

/**
 * Reflect: a journal page builds itself. The canvas appears, the reflection is written
 * into it, then stickies and photos drop onto the page one by one.
 * Phones get the same pieces in a tidy stacked flow instead of a scattered canvas.
 */
function Reflect({ auto, playing }: PanelProps) {
  const [scripted] = useState(auto);
  const t = useStoryClock(playing && scripted);
  const typed = scripted ? Math.max(0, Math.min(REFLECT_PAGE.answer.length, Math.floor((t - 700) / 26))) : REFLECT_PAGE.answer.length;
  const on = (ms: number) => !scripted || t > ms;
  // Drop order: core insight, city photo, what surfaced, coffee photo, then the heart.
  const drops = [2500, 2950, 3400, 3850, 4300];

  return (
    <div
      className={`lp-canvas relative w-full max-w-[640px] ${scripted ? "lp-pop" : ""}`}
    >
      <div className="relative flex flex-col gap-4 p-4 sm:block sm:h-[340px] sm:p-0">
        {/* Header */}
        <div className="sm:absolute sm:left-[5%] sm:top-[6%]">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-(--lp-muted)">{REFLECT_PAGE.date}</p>
          <p className="mt-1 font-serif text-xl italic text-(--lp-ink) sm:text-[22px]">{REFLECT_PAGE.title}</p>
        </div>

        {/* The reflection, written live */}
        <div className="lp-answer relative sm:absolute sm:left-[5%] sm:top-[27%] sm:w-[50%]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-(--lp-accent)">Your reflection</p>
          <p className="mt-1.5 min-h-[4.2em] text-[14px] leading-[1.5] text-(--lp-body) sm:text-[15px]">
            <span className="sr-only">{REFLECT_PAGE.answer}</span>
            <span aria-hidden>{REFLECT_PAGE.answer.slice(0, typed)}</span>
            {scripted && typed < REFLECT_PAGE.answer.length && <span className="lp-caret lp-caret-blink" aria-hidden />}
          </p>
          <span aria-hidden className="lp-doodle-heart lp-drop absolute -right-3 -top-3" data-on={on(drops[4])} style={{ "--r": "-8deg" } as CSSProperties}>
            ♡
          </span>
        </div>

        {/* Stickies and photos drop onto the page */}
        <div className="flex flex-wrap items-start justify-center gap-3 sm:contents">
          <div
            className={`lp-sticky-note lp-tone-${REFLECT_PAGE.stickies[0].tone} lp-drop relative w-[46%] sm:absolute sm:left-[61%] sm:top-[9%] sm:w-[33%]`}
            data-on={on(drops[0])}
            style={{ "--r": "-3deg" } as CSSProperties}
          >
            <span className="lp-sticky-tape" aria-hidden />
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-80">{REFLECT_PAGE.stickies[0].label}</p>
            <p className="mt-1 font-['Kalam'] text-[15px] leading-tight sm:text-base">{REFLECT_PAGE.stickies[0].text}</p>
          </div>
          <figure
            className="lp-polaroid lp-drop w-[30%] sm:absolute sm:left-[64%] sm:top-[45%] sm:w-[18%]"
            data-on={on(drops[1])}
            style={{ "--r": "5deg" } as CSSProperties}
            aria-hidden
          >
            <span className="block w-full rounded-[3px]" style={cropStyle(PHOTOS[0].crop)} />
            <figcaption className="mt-1 text-center font-['Kalam'] text-[11px] text-(--lp-body)">{PHOTOS[0].caption}</figcaption>
          </figure>
          <div
            className={`lp-sticky-note lp-tone-${REFLECT_PAGE.stickies[1].tone} lp-drop relative w-[46%] sm:absolute sm:left-[31%] sm:top-[63%] sm:w-[29%]`}
            data-on={on(drops[2])}
            style={{ "--r": "2deg" } as CSSProperties}
          >
            <span className="lp-sticky-tape" aria-hidden />
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-80">{REFLECT_PAGE.stickies[1].label}</p>
            <p className="mt-1 font-['Kalam'] text-[15px] leading-tight sm:text-base">{REFLECT_PAGE.stickies[1].text}</p>
          </div>
          <figure
            className="lp-polaroid lp-drop w-[30%] sm:absolute sm:left-[8%] sm:top-[61%] sm:w-[17%]"
            data-on={on(drops[3])}
            style={{ "--r": "-5deg" } as CSSProperties}
            aria-hidden
          >
            <span className="block w-full rounded-[3px]" style={cropStyle(PHOTOS[1].crop)} />
            <figcaption className="mt-1 text-center font-['Kalam'] text-[11px] text-(--lp-body)">{PHOTOS[1].caption}</figcaption>
          </figure>
        </div>
      </div>
    </div>
  );
}

/** A month folder: back panel with its tab, whatever sits inside, and the front panel. */
function Folder({
  back,
  front,
  size,
  children,
  label,
}: {
  back: string;
  front: string;
  size: "sm" | "lg";
  children?: ReactNode;
  label: ReactNode;
}) {
  return (
    <div className={`lp-folder lp-folder-${size}`} style={{ "--fb": back, "--ff": front } as CSSProperties}>
      <div className="lp-folder-back" />
      {children}
      <div className="lp-folder-front">{label}</div>
    </div>
  );
}

function Progress({ done, total }: { done: number; total: number }) {
  return (
    <span className="lp-folder-progress">
      <span className="lp-folder-count">
        {done} of {total} done
      </span>
      <span className="lp-folder-bar" aria-hidden>
        <span style={{ transform: `scaleX(${done / total})` }} />
      </span>
    </span>
  );
}

/**
 * Your monthly win: this month's next steps rise out of its folder, get ticked off one by
 * one while the folder's progress fills, and the win lands. Past months sit beside it with
 * their papers peeking out, as on the Momentum page.
 */
function MonthlyWin({ onInteract, auto, playing }: PanelProps) {
  const [scripted] = useState(auto);
  const [manual, setManual] = useState<boolean[] | null>(null);
  const t = useStoryClock(playing && scripted);
  const risen = MONTH_TASKS.map((_, i) => !scripted || t > 900 + i * 260);
  const autoCount = t < 2500 ? 0 : Math.min(MONTH_TASKS.length, 1 + Math.floor((t - 2500) / 700));
  const done = manual ?? (scripted ? MONTH_TASKS.map((_, i) => i < autoCount) : [true, true, false]);
  const doneCount = done.filter(Boolean).length;
  const allDone = doneCount === MONTH_TASKS.length;
  /** Entrance for a folder column (only when the panel is telling its story). */
  const pop = (base: string, ms: number) => ({
    className: scripted ? `${base} lp-pop` : base,
    style: scripted ? popAt(ms) : undefined,
  });

  return (
    <div className="relative flex w-full max-w-[680px] flex-col items-center">
      <div className="flex min-h-11 w-full items-center justify-center">
        {allDone && (
          <p className="lp-pop relative inline-flex min-h-11 items-center gap-2.5 rounded-full bg-(--lp-ink) px-[18px] text-[15px] font-medium text-white">
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
              <path d="M12 2c.8 4.8 2.7 7.4 8 9-5.3 1.6-7.2 4.2-8 9-.8-4.8-2.7-7.4-8-9 5.3-1.6 7.2-4.2 8-9Z" fill="#B8A6FF" />
            </svg>
            This month’s win, locked in.
            <span className="lp-burst -right-4 -top-4" aria-hidden />
          </p>
        )}
      </div>

      {/* pt reserves the space the rising papers fill above the folders. */}
      <div className="mt-2 flex w-full items-end justify-center gap-4 pt-[104px] sm:gap-7">
        {MONTH_FOLDERS.map((f, i) => (
          <div key={f.month} {...pop("hidden flex-col items-center gap-2.5 sm:flex", i * 110)}>
            <Folder back={f.back} front={f.front} size="sm" label={<Progress done={f.done} total={f.total} />}>
              <span className="lp-peek" style={{ rotate: "-6deg", left: "18%" }} aria-hidden />
              <span className="lp-peek" style={{ rotate: "5deg", left: "42%" }} aria-hidden />
            </Folder>
            <span className="text-[15px] text-(--lp-muted) sm:text-base">{f.month}</span>
          </div>
        ))}

        <div {...pop("flex flex-col items-center gap-2.5", 240)}>
          <Folder
            back={CURRENT_FOLDER.back}
            front={CURRENT_FOLDER.front}
            size="lg"
            label={<Progress done={doneCount} total={MONTH_TASKS.length} />}
          >
            <ul className="lp-papers" aria-label="This month’s next steps">
              {MONTH_TASKS.map((task, i) => (
                <li
                  key={task}
                  className="lp-paper"
                  data-risen={risen[i]}
                  style={{ "--k": MONTH_TASKS.length - 1 - i, "--tilt": `${[-2.5, 1.8, -1.2][i]}deg`, zIndex: i + 1 } as CSSProperties}
                >
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={done[i]}
                    onClick={() => {
                      onInteract();
                      setManual(done.map((v, j) => (j === i ? !v : v)));
                    }}
                    className="flex min-h-11 w-full items-center gap-2.5 px-3 text-left text-[13px] text-(--lp-body) sm:text-sm"
                  >
                    <span className="lp-check" style={{ width: 20, height: 20 }} aria-hidden>
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <span className="lp-check-label truncate">{task}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Folder>
          <span className="flex items-center gap-2 text-[15px] font-medium text-(--lp-ink) sm:text-base">
            {CURRENT_FOLDER.month}
            <span className="rounded-full bg-(--lp-lavender) px-2 py-0.5 text-[10px] font-semibold tracking-[0.14em] text-(--lp-accent)">
              • NOW
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}

const PANELS = [DropItIn, UnfoldIt, Reflect, MonthlyWin];

/**
 * "From reflection to action." A marketing demonstration only; nothing here calls the API.
 *
 * Auto-play: each step holds for 6.6s (V6), shown by the bar filling across its tab, and
 * plays a short story in the panel; when the bar finishes, the next step opens and the
 * cycle repeats. Any click or key press on a tab, trigger or next step pauses auto-play
 * and keeps the visitor's choice; after 4s without further interaction it restarts from
 * the current step. It also pauses off screen and in a hidden tab. Reduced motion: manual
 * only, nothing plays by itself.
 */
export function HowItWorksDemo() {
  const [step, setStep] = useState(0);
  const [auto, setAuto] = useState(true);
  /** Bumped whenever auto-play restarts, so the bar and the panel's story start over. */
  const [cycle, setCycle] = useState(0);
  const resumeTimer = useRef<number | undefined>(undefined);
  const sectionRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const reduce = useLandingReducedMotion();
  const onScreen = useOnScreen(sectionRef, 0.3);
  const pageVisible = usePageVisible();

  const autoPlay = auto && !reduce;
  const running = autoPlay && onScreen && pageVisible;
  const Panel = PANELS[step];

  useEffect(() => () => window.clearTimeout(resumeTimer.current), []);

  const interact = () => {
    setAuto(false);
    window.clearTimeout(resumeTimer.current);
    resumeTimer.current = window.setTimeout(() => {
      setAuto(true);
      setCycle((c) => c + 1);
    }, RESUME_AFTER_MS);
  };

  const choose = (i: number) => {
    interact();
    setStep(i);
  };

  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const last = DEMO_STEPS.length - 1;
    let next: number | null = null;
    if (e.key === "ArrowRight") next = step === last ? 0 : step + 1;
    if (e.key === "ArrowLeft") next = step === 0 ? last : step - 1;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = last;
    if (next === null) return;
    e.preventDefault();
    choose(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <section
      ref={sectionRef}
      id="action"
      aria-labelledby="lp-action-title"
      data-paused={!onScreen || !pageVisible}
      className="relative py-20 md:pb-[110px] md:pt-[100px]"
    >
      <div className="mx-auto max-w-[1200px] px-4 sm:px-8 lg:px-12">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between md:gap-10">
          <div className="flex flex-col gap-[18px]">
            <p className="lp-eyebrow">How it works</p>
            <h2 id="lp-action-title" className="lp-h2">
              From reflection to&nbsp;<span className="lp-accent">action.</span>
            </h2>
          </div>
          <p className="lp-lede md:max-w-[360px]">
            {nw("Every Unfolding ends with next steps drawn from what you wrote, not from a template.")}
          </p>
        </div>

        <div role="tablist" aria-label="How it works" className="mt-10 flex gap-3 sm:gap-[22px] md:mt-[50px]">
          {DEMO_STEPS.map((s, i) => (
            <button
              key={s.title}
              ref={(el) => {
                tabRefs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`lp-step-tab-${i}`}
              aria-selected={step === i}
              aria-controls="lp-step-panel"
              tabIndex={step === i ? 0 : -1}
              onClick={() => choose(i)}
              onKeyDown={onTabKey}
              className="lp-steptab"
            >
              {autoPlay && step === i && (
                <span
                  key={`${step}-${cycle}`}
                  className="lp-tbar"
                  data-running={running}
                  aria-hidden
                  onAnimationEnd={() => setStep((s) => (s + 1) % DEMO_STEPS.length)}
                />
              )}
              <span className="text-base font-medium tracking-[-0.01em] sm:text-[22px]">{s.title}</span>
              <span className="hidden text-base leading-[1.45] sm:block">{s.desc}</span>
            </button>
          ))}
        </div>

        <div
          id="lp-step-panel"
          role="tabpanel"
          aria-labelledby={`lp-step-tab-${step}`}
          className="lp-stage mt-6 flex min-h-[420px] flex-col items-center justify-center gap-6 px-4 py-10 sm:mt-[34px] sm:px-10 md:min-h-[560px] md:px-[60px] md:py-11"
        >
          <p className="relative text-center text-base text-(--lp-muted) sm:sr-only">{DEMO_STEPS[step].desc}</p>
          <div key={`${step}-${cycle}`} className="lp-pop relative flex w-full justify-center">
            <Panel onInteract={interact} auto={autoPlay} playing={running} />
          </div>
        </div>
      </div>
    </section>
  );
}
